"use client";

import * as React from "react";
import { LucideIcon, CheckCircle, Circle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

// ============================================================================
// ProgressCard - Card showing progress towards a goal
// ============================================================================

export interface ProgressCardProps {
  /** Title text */
  title: string;
  /** Current progress value (0-100) */
  progress: number;
  /** Optional description */
  description?: string;
  /** Show fraction (e.g., "3/5 completed") */
  fraction?: {
    current: number;
    total: number;
    label?: string;
  };
  /** Color variant */
  variant?: "default" | "emerald" | "blue" | "amber" | "purple";
  /** Whether to show sparkle effect at 100% */
  celebrateComplete?: boolean;
  /** Additional className */
  className?: string;
}

const progressVariants = {
  default: {
    badge: "",
    text: "text-foreground",
  },
  emerald: {
    badge: "text-emerald-600",
    text: "text-emerald-600",
  },
  blue: {
    badge: "text-blue-600",
    text: "text-blue-600",
  },
  amber: {
    badge: "text-amber-600",
    text: "text-amber-600",
  },
  purple: {
    badge: "text-purple-600",
    text: "text-purple-600",
  },
};

export function ProgressCard({
  title,
  progress,
  description,
  fraction,
  variant = "default",
  celebrateComplete = true,
  className,
}: ProgressCardProps) {
  const styles = progressVariants[variant];
  const isComplete = progress >= 100;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">{title}</span>
        <Badge variant="outline" className={cn("text-sm font-semibold px-2", styles.badge)}>
          {Math.round(progress)}%
        </Badge>
      </div>
      
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      
      <div className="relative">
        <Progress value={progress} className="h-2" />
        {celebrateComplete && isComplete && (
          <Sparkles className="absolute -right-1 -top-1 h-4 w-4 text-yellow-500 animate-pulse" />
        )}
      </div>
      
      {fraction && (
        <p className="text-xs text-muted-foreground">
          {fraction.current} of {fraction.total} {fraction.label || "completed"}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// ProgressSection - Section showing multiple progress items
// ============================================================================

export interface ProgressSectionItem {
  id: string;
  label: string;
  progress: number;
  icon?: LucideIcon;
}

export interface ProgressSectionProps {
  items: ProgressSectionItem[];
  columns?: 2 | 3 | 4;
  className?: string;
}

export function ProgressSection({
  items,
  columns = 2,
  className,
}: ProgressSectionProps) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4",
  };

  return (
    <div className={cn("grid gap-2", gridCols[columns], className)}>
      {items.map((item) => {
        const isComplete = item.progress >= 100;
        const Icon = item.icon;
        
        return (
          <div 
            key={item.id}
            className={cn(
              "p-3 rounded-xl border transition-colors",
              isComplete 
                ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800" 
                : "bg-muted/30 border-border"
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              {Icon && (
                <Icon className={cn(
                  "h-4 w-4",
                  isComplete ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                )} />
              )}
              <span className="text-xs font-medium truncate">
                {item.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={item.progress} className="h-1.5 flex-1" />
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {Math.round(item.progress)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// ChecklistProgress - Progress shown as checklist items
// ============================================================================

export interface ChecklistItem {
  id: string;
  label: string;
  description?: string;
  completed: boolean;
  required?: boolean;
}

export interface ChecklistProgressProps {
  items: ChecklistItem[];
  showCount?: boolean;
  className?: string;
}

export function ChecklistProgress({
  items,
  showCount = true,
  className,
}: ChecklistProgressProps) {
  const completed = items.filter(i => i.completed).length;
  const total = items.length;

  return (
    <div className={cn("space-y-3", className)}>
      {showCount && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium">{completed} of {total}</span>
        </div>
      )}
      
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              "flex items-start gap-3 p-2 rounded-lg",
              item.completed && "opacity-60"
            )}
          >
            {item.completed ? (
              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-sm",
                  item.completed ? "line-through text-muted-foreground" : "font-medium"
                )}>
                  {item.label}
                </span>
                {item.required && !item.completed && (
                  <Badge variant="outline" className="text-[10px] px-1">Required</Badge>
                )}
              </div>
              {item.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                  {item.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

